/**
 * Error Tracking Service - Stub Implementation
 * 
 * This is a placeholder service that logs errors to console.
 * To enable production error tracking with Sentry:
 * 
 * 1. Install: npm install @sentry/react
 * 2. Create account at https://sentry.io
 * 3. Create a React project and get your DSN
 * 4. Add VITE_SENTRY_DSN to your .env file
 * 5. See docs/sentry-setup.md for full integration code
 * 
 * For now, all errors are logged to console in development.
 */

let isInitialized = false;

interface ErrorContext {
  userId?: string;
  email?: string;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

/**
 * Initialize error tracking
 * Currently logs setup instructions - implement Sentry when ready
 */
export async function initErrorTracking(): Promise<void> {
  // Only show setup hint in production when DSN is configured
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (dsn) {
    console.log(
      '[ErrorTracking] VITE_SENTRY_DSN is configured but Sentry is not installed.\n' +
      'To enable: npm install @sentry/react, then update this service.'
    );
  } else if (!import.meta.env.DEV) {
    console.log('[ErrorTracking] For production error monitoring, set up Sentry.');
  }
  
  isInitialized = true;
}

/**
 * Set user context for error tracking
 * Stub: logs to console for now
 */
export function setErrorTrackingUser(context: ErrorContext): void {
  if (!isInitialized) return;
  console.log('[ErrorTracking] User context set:', context.userId);
}

/**
 * Clear user context (on logout)
 */
export function clearErrorTrackingUser(): void {
  if (!isInitialized) return;
  console.log('[ErrorTracking] User context cleared');
}

/**
 * Capture an exception with optional context
 * Stub: logs to console for now
 */
export function captureException(
  error: Error | unknown,
  context?: ErrorContext
): void {
  // Log to console (in production, this would go to Sentry)
  console.error('[ErrorTracking] Captured exception:', error);
  if (context?.tags) {
    console.error('[ErrorTracking] Tags:', context.tags);
  }
  if (context?.extra) {
    console.error('[ErrorTracking] Extra:', context.extra);
  }
}

/**
 * Capture a message (for non-error events)
 * Stub: logs to console for now
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  _context?: ErrorContext
): void {
  const logFn = level === 'error' ? console.error : level === 'warning' ? console.warn : console.log;
  logFn(`[ErrorTracking] ${level}: ${message}`);
}

/**
 * Add breadcrumb for debugging
 * Stub: no-op for now
 */
export function addBreadcrumb(
  _message: string,
  _category: string,
  _data?: Record<string, unknown>
): void {
  // No-op in stub implementation
}

/**
 * Start a performance transaction
 * Stub: returns no-op finish function
 */
export function startTransaction(
  _name: string,
  _op: string
): { finish: () => void } {
  return { finish: () => {} };
}

/**
 * React Error Boundary wrapper
 * Returns null since Sentry is not installed
 */
export function getErrorBoundary(): null {
  return null;
}
